import {gql} from '@apollo/client';

import {doQuerySafe, readFragmentFromCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {LookupStructuredTableRowMetadata} from 'notebook/integrations/lookup/LookupStructuredTableAdapter';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {
  getTypedValueFromBioentitySummary,
  getTypedValueFromStorableSummaries,
} from 'notebook/tables/typed-values/DataTypes';
import APIUtil from 'util/APIUtil';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {ColumnGroup} from '../columns/ColumnDefinitions';
import {LiveCellValue} from '../LiveCellValues';
import {
  SampleLookupInfo,
  StructuredTableColumn,
  StructuredTableRow,
  StructuredTableRowUpdater,
} from '../StructuredTableConstants';
import updateCellWithLiveValue from '../util/updateCellWithLiveValue';
import {
  BulkBioentityInfoForRowUpdateQuery,
  BulkBioentityInfoForRowUpdateQueryVariables,
} from './__generated/BulkBioentityInfoForRowUpdateQuery';
import {
  BulkStorableInfoForRowUpdateQuery,
  BulkStorableInfoForRowUpdateQueryVariables,
} from './__generated/BulkStorableInfoForRowUpdateQuery';

type BioentityInfo = ExtractByTypename<
  BulkBioentityInfoForRowUpdateQuery['bioentities'][number],
  'BasicFolderItemNode'
>;

type StorableInfo = ExtractByTypename<BulkStorableInfoForRowUpdateQuery['storables'][number], 'BoxNode'>;

export const BULK_BIOENTITY_INFO_QUERY = gql`
  query BulkBioentityInfoForRowUpdateQuery($bioentityIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: NOTEBOOK) {
    bioentities(ids: $bioentityIds) {
      ...LookupBioentityGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('LookupBioentity').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const BULK_STORABLE_INFO_QUERY = gql`
  query BulkStorableInfoForRowUpdateQuery($storableIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: NOTEBOOK) {
    storables(ids: $storableIds) {
      ...LookupStorableGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('LookupStorable').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

async function _bulkFetchModel(
  sampleLookupInfos: Array<SampleLookupInfo>
): Promise<Array<BioentityInfo | StorableInfo | InaccessibleInfoGraphQL>> {
  const ids = sampleLookupInfos.reduce(
    (
      ids: {
        bioentityIds: Array<string>;
        storableIds: Array<string>;
      },
      info
    ) => {
      if (APIUtil.isBiologicalEntity(info.idForLookup)) {
        ids.bioentityIds.push(info.idForLookup);
      } else {
        ids.storableIds.push(info.idForLookup);
      }
      return ids;
    },
    {bioentityIds: [], storableIds: []}
  );

  const results: Array<BioentityInfo | StorableInfo | InaccessibleInfoGraphQL> = [];
  const bioentityPromise = async (): Promise<void> => {
    if (ids.bioentityIds.length > 0) {
      const {bioentities} = await doQuerySafe<
        BulkBioentityInfoForRowUpdateQuery,
        BulkBioentityInfoForRowUpdateQueryVariables
      >(
        {query: BULK_BIOENTITY_INFO_QUERY},
        {
          variables: {
            bioentityIds: ids.bioentityIds,
          },
          fetchPolicy: 'network-only',
        }
      );
      results.push(...bioentities);
    }
  };
  const storablePromise = async (): Promise<void> => {
    if (ids.storableIds.length > 0) {
      const {storables} = await doQuerySafe<
        BulkStorableInfoForRowUpdateQuery,
        BulkStorableInfoForRowUpdateQueryVariables
      >(
        {query: BULK_STORABLE_INFO_QUERY},
        {
          variables: {
            storableIds: ids.storableIds,
          },
          fetchPolicy: 'network-only',
        }
      );
      results.push(...storables);
    }
  };
  await Promise.all([bioentityPromise(), storablePromise()]);
  return results;
}

const LookupStructuredTableRowUpdater: StructuredTableRowUpdater<
  BioentityInfo | StorableInfo,
  LookupStructuredTableRowMetadata
> = {
  fetchModel(
    sampleLookupInfo: SampleLookupInfo
  ): Promise<BioentityInfo | StorableInfo | InaccessibleInfoGraphQL> {
    return _bulkFetchModel([sampleLookupInfo]).then((results) => results[0]);
  },

  bulkFetchModel(
    sampleLookupInfos: Array<SampleLookupInfo>
  ): Promise<Array<BioentityInfo | StorableInfo | InaccessibleInfoGraphQL>> {
    return _bulkFetchModel(sampleLookupInfos);
  },

  updateCell(
    model: BioentityInfo | StorableInfo | InaccessibleInfoGraphQL,
    _: ColumnGroup | null,
    column: StructuredTableColumn
  ): LiveCellValue {
    if (isInaccessible(model)) {
      return 'NO_VALUE';
    }
    if (column.columnType === TableColumnType.EXISTING_BIOENTITY) {
      return getTypedValueFromBioentitySummary(model);
    } else if (
      [TableColumnType.PLATE, TableColumnType.BOX, TableColumnType.CONTAINERS].includes(column.columnType)
    ) {
      return getTypedValueFromStorableSummaries([model]);
    }
    return 'NO_VALUE';
  },

  updateRow(
    model: BioentityInfo | InaccessibleInfoGraphQL,
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<LookupStructuredTableRowMetadata>,
    columnGroup: ColumnGroup | null
  ): StructuredTableRow<LookupStructuredTableRowMetadata> {
    if (isInaccessible(model)) {
      return row;
    }
    const cells = row.cells.map((cell, columnIndex) => {
      const column = columns[columnIndex];
      const liveValue = this.updateCell(model, columnGroup, column);
      return updateCellWithLiveValue(cell, liveValue);
    });

    return {
      ...row,
      cells,
    };
  },

  readFromCache({idForLookup: apiIdentifier}: SampleLookupInfo): BioentityInfo | StorableInfo | null {
    return readFragmentFromCache(
      APIUtil.isBiologicalEntity(apiIdentifier)
        ? getFragmentWrapper('LookupBioentity')
        : getFragmentWrapper('LookupStorable'),
      apiIdentifier
    );
  },
};

export default LookupStructuredTableRowUpdater;
